/**
 * Which album, asked before the file picker rather than after the upload.
 *
 * Most people are looking at All albums when they press "Add photos" — it is
 * the default view — and the folder came from the URL, which has none there. So
 * the photographs went in unfiled, silently, and the tidying happened weeks
 * later if at all. Nothing was broken; the question simply was never asked.
 *
 * Asked once per batch, and answerable in one tap. "Not in an album" is a real
 * answer and stays first, because a screenshot or a receipt genuinely does not
 * belong anywhere and making that the awkward path teaches people to file
 * things wrongly.
 *
 * Most recent first: the album somebody is adding to is nearly always the one
 * they made or touched most recently, and alphabetical answers a question
 * nobody asked.
 */

import { useMemo, useState } from "react";
import { useT } from "../../lib/i18n.js";
import { EmojiIcon } from "../../components/ui/EmojiIcon.js";
import { Input } from "../../components/ui/Input.js";
import { Sheet } from "../../components/ui/Sheet.js";
import { flagFor, primaryCountry } from "../../lib/countries.js";
import { byYearThen } from "./order.js";
import type { Album } from "./types.js";
import "./AlbumPicker.css";

/** Enough albums that finding one by eye stops being instant. */
const SEARCH_THRESHOLD = 6;

export function AlbumPicker({
  albums,
  canCreate,
  onChoose,
  onCreateAlbum,
  onClose,
}: {
  albums: Album[];
  canCreate: boolean;
  /** Null means "not in an album", which is a choice and not a refusal. */
  onChoose: (folderId: string | null) => void;
  onCreateAlbum: () => void;
  onClose: () => void;
}) {
  const t = useT();
  const [query, setQuery] = useState("");

  const needle = query.trim().toLocaleLowerCase();

  const shown = useMemo(() => {
    const matching =
      needle.length === 0
        ? albums
        : albums.filter((album) =>
            album.name.toLocaleLowerCase().includes(needle),
          );

    /*
     * Newest year first, then most recently touched inside a year — see the
     * note at the top about why not alphabetical.
     */
    return [...matching].sort(
      byYearThen(
        (a, b) =>
          new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime(),
      ),
    );
  }, [albums, needle]);

  return (
    <Sheet open onClose={onClose} title={t("Add to which album?")} size="half">
      {albums.length > SEARCH_THRESHOLD && (
        <Input
          value={query}
          type="search"
          autoComplete="off"
          placeholder={t("Search albums")}
          onChange={(event) => setQuery(event.target.value)}
        />
      )}

      <ul className="fc-albumpick" role="list">
        {canCreate && (
          <li>
            <button
              type="button"
              className="fc-albumpick__new"
              onClick={onCreateAlbum}
            >
              <span className="fc-albumpick__icon" aria-hidden="true">
                +
              </span>
              <span className="fc-albumpick__label">
                <span className="fc-albumpick__title">{t("New album")}</span>
              </span>
            </button>
          </li>
        )}

        {shown.map((album) => (
          <li key={album.id}>
            <button type="button" onClick={() => onChoose(album.id)}>
              <span className="fc-albumpick__icon" aria-hidden="true">
                {album.emoji ? (
                  <EmojiIcon emoji={album.emoji} />
                ) : primaryCountry(album.countryCodes) ? (
                  <EmojiIcon
                    emoji={flagFor(primaryCountry(album.countryCodes)!)}
                  />
                ) : (
                  "🗂"
                )}
              </span>

              <span className="fc-albumpick__label">
                <span className="fc-albumpick__title">{album.name}</span>
                <span className="fc-albumpick__count fc-tabular">
                  {album.photoCount + album.videoCount}{" "}
                  {album.photoCount + album.videoCount === 1
                    ? t("item")
                    : t("items")}
                </span>
              </span>
            </button>
          </li>
        ))}

        {/*
          Last, and still plainly offered. A screenshot or a receipt genuinely
          belongs nowhere, and making that the awkward path is how people learn
          to file things in whichever album is nearest.
        */}
        <li>
          <button
            type="button"
            className="fc-albumpick__none"
            onClick={() => onChoose(null)}
          >
            <span className="fc-albumpick__icon" aria-hidden="true">
              —
            </span>
            <span className="fc-albumpick__label">
              <span className="fc-albumpick__title">
                {t("Not in an album")}
              </span>
            </span>
          </button>
        </li>
      </ul>
    </Sheet>
  );
}
